import React, { useState, useEffect } from "react";
import { Search, LogOut, ChevronLeft, ChevronRight,Trash2  } from "lucide-react";
import "../Styles/subscriberlist.css";

const API_BASE_URL = "";

// ---- Dummy data (used when the API is unreachable, so the UI can be
// previewed before the backend endpoints exist) ----
const DUMMY_SUBSCRIBERS = [
  { id: 1, email: "suganthj.gowtham@gmail.com", subscribedAt: "2026-05-27T10:48:29.000Z" },
  { id: 2, email: "priya.raghavan@gmail.com", subscribedAt: "2026-05-26T09:12:04.000Z" },
  { id: 3, email: "arun.kumar@gmail.com", subscribedAt: "2026-05-25T18:30:11.000Z" },
  { id: 4, email: "divya.shree@gmail.com", subscribedAt: "2026-05-25T14:02:47.000Z" },
  { id: 5, email: "karthik.raja@gmail.com", subscribedAt: "2026-05-24T21:15:33.000Z" },
  { id: 6, email: "meena.iyer@gmail.com", subscribedAt: "2026-05-24T08:44:19.000Z" },
  { id: 7, email: "vignesh.babu@gmail.com", subscribedAt: "2026-05-23T16:27:58.000Z" },
  { id: 8, email: "anitha.suresh@gmail.com", subscribedAt: "2026-05-22T11:03:22.000Z" },
  { id: 9, email: "rahul.nair@gmail.com", subscribedAt: "2026-05-21T19:41:07.000Z" },
  { id: 10, email: "swathi.ramesh@gmail.com", subscribedAt: "2026-05-20T07:55:40.000Z" },
  { id: 11, email: "bala.subramaniam@gmail.com", subscribedAt: "2026-05-19T13:18:52.000Z" },
  { id: 12, email: "keerthana.v@gmail.com", subscribedAt: "2026-05-18T22:09:14.000Z" },
  { id: 13, email: "prasanna.venkat@gmail.com", subscribedAt: "2026-05-17T15:36:28.000Z" },
  { id: 14, email: "lavanya.krishnan@gmail.com", subscribedAt: "2026-05-16T10:20:05.000Z" },
  { id: 15, email: "gokul.anand@gmail.com", subscribedAt: "2026-05-15T06:48:39.000Z" },
];

export default function SubscribersList() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [query, setQuery] = useState("");

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/subscribers`);
      if (!res.ok) throw new Error("Failed to load subscribers");
      const data = await res.json();
      setSubscribers(data);
      setError(null);
    } catch (err) {
      // API not reachable yet — fall back to dummy data so the UI can be previewed
      console.warn("Falling back to dummy subscribers:", err.message);
      setSubscribers(DUMMY_SUBSCRIBERS);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const filteredSubscribers = subscribers.filter((s) => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    return [s.email, s.subscribedAt]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(q));
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [query, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredSubscribers.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const paginatedSubscribers = filteredSubscribers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) pages.push("dots-start");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("dots-end");
    pages.push(totalPages);
    return pages;
  };

  const toggleAll = () => {
    setSelectAll((v) => !v);
    setSelected(selectAll ? [] : filteredSubscribers.map((s) => s.id));
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this subscriber? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/subscribers/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete");
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
      setSelected((prev) => prev.filter((x) => x !== id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete subscriber. Please try again.");
    }
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} selected subscriber(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/subscribers/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selected }),
      });
      if (!res.ok) throw new Error("Failed to delete selected subscribers");
      setSubscribers((prev) => prev.filter((s) => !selected.includes(s.id)));
      setSelected([]);
      setSelectAll(false);
    } catch (err) {
      console.error(err);
      alert("Failed to delete selected subscribers. Please try again.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const total = subscribers.length;

  const formatDate = (iso) => {
    if (!iso) return "—";
    // Display exactly like the Figma reference: YYYY-MM-DDTHH:MM:SS.sssZ
    return iso;
  };

  return (
    <div className="sub-app">
      {/* ---------------- Main content ---------------- */}
      <main className="sub-main">
        <header className="sub-topbar sub-panel">
          <h1>Subscribers</h1>
          <button className="sub-logout">
            <LogOut size={14} />
            Logout
          </button>
        </header>

        <section className="sub-panel">
          <div className="sub-controls">
            <label className="sub-show-entries">
              Show Entries :
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <span className="sub-pill sub-pill--total">Total Count : {total}</span>

           <button className="sub-pill sub-pill--export">
  <span className="sub-excel-icon-wrap">
    <img
      src="/assets/excel.png"
      alt="Excel"
      className="sub-excel-icon sub-excel-icon--default"
    />
    <img
      src="/assets/excel-white.png"
      alt="Excel"
      className="sub-excel-icon sub-excel-icon--hover"
    />
  </span>
  Export To Excel
</button>

            {selected.length > 0 && (
              <button
                className="sub-pill sub-pill--bulk-delete"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? "Deleting..." : `Delete Selected (${selected.length})`}
              </button>
            )}

            <div className="sub-search">
              <Search size={14} />
              <input
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="sub-table-outer">
            <div className="sub-table-inner">
              {loading ? (
                <p style={{ padding: "20px" }}>Loading subscribers...</p>
              ) : error ? (
                <p style={{ padding: "20px", color: "#e0432c" }}>{error}</p>
              ) : (
                <table className="sub-table">
                  <thead>
                    <tr>
                      <th className="sub-checkbox-col">
                        <label className="sub-select-all">
                          <input type="checkbox" checked={selectAll} onChange={toggleAll} />
                          <span>Select All</span>
                        </label>
                      </th>
                      <th>S.No</th>
                      <th>Email</th>
                      <th>Subscribed At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSubscribers.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: "20px", textAlign: "center" }}>
                          No subscribers yet.
                        </td>
                      </tr>
                    ) : (
                      paginatedSubscribers.map((s, index) => (
                        <tr key={s.id}>
                          <td className="sub-checkbox-col">
                            <input
                              type="checkbox"
                              checked={selected.includes(s.id)}
                              onChange={() => toggleOne(s.id)}
                            />
                          </td>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>{s.email}</td>
                          <td>{formatDate(s.subscribedAt)}</td>
                          <td>
                            <button
  className="sub-icon-btn sub-icon-btn--delete"
  title="Delete"
  onClick={() => handleDelete(s.id)}
>
  <Trash2 size={13} />
  Delete
</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="sub-table-footer">
            <span>
              Showing {paginatedSubscribers.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
              &ndash;
              {(currentPage - 1) * pageSize + paginatedSubscribers.length} of {filteredSubscribers.length} Members
            </span>
            <div className="sub-pagination">
              <button
                className="sub-page-btn"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={14} />
              </button>
              {getPageNumbers().map((p, i) =>
                p === "dots-start" || p === "dots-end" ? (
                  <span key={`${p}-${i}`} className="sub-page-dots">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`sub-page-btn${p === currentPage ? " active" : ""}`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="sub-page-btn"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
